// Looks up the NHRA class index (1/4 or 1/8 mile) for a race.
//
// Distance rule (per product requirement):
//   - If the race date is *today*, assume a 1/4-mile race -> use the 1/4 value.
//   - Otherwise (a previous day), decide by the 1/4-mile ET field: if it holds a
//     real value the race went to 1/4 mile, else it was an 1/8-mile race.
//
// Returns the index formatted to 2 decimals (e.g. "9.45"), or null when the
// class isn't in the table or no value applies (callers render "-").

import nhraIndexes from "../data/nhraIndexes.json";

// Class lookup tolerant of casing / surrounding whitespace.
const NORMALIZED = {};
for (const [key, val] of Object.entries(nhraIndexes.indexes)) {
  NORMALIZED[key.trim().toUpperCase()] = val;
}

const lookupClass = (carClass) => {
  if (!carClass || carClass === "NA" || carClass === "-") return null;
  return (
    nhraIndexes.indexes[carClass] ||
    NORMALIZED[String(carClass).trim().toUpperCase()] ||
    null
  );
};

const MONTHS = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

// Tolerant parser for the various date strings the app sees. The date portion
// is always first; the rest (time, microseconds, timezone) is ignored:
//   - raceUniqueId (Slip):  "MM-DD-YYYY_HH-MM-SS-micros_TZ"  e.g. 05-22-2026_15-57-20-913322_EDT-0400
//   - live timestamp:       "MM-DD-YYYY HH:MM:SS"
//   - others:               YYYY-MM-DD, DD-MMM-YYYY, etc.
// Split on space / T / underscore so the leading date is isolated in every case.
const parseRaceDate = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s || s === "NA" || s === "-") return null;
  const datePart = s.split(/[ T_]/)[0];
  let m;
  if ((m = datePart.match(/^(\d{1,2})[-/]([A-Za-z]{3})[-/](\d{4})$/))) {
    const mo = MONTHS[m[2].toLowerCase()];
    if (mo != null) return new Date(+m[3], mo, +m[1]);
  }
  if ((m = datePart.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/))) {
    return new Date(+m[3], +m[1] - 1, +m[2]);
  }
  if ((m = datePart.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/))) {
    return new Date(+m[1], +m[2] - 1, +m[3]);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const isToday = (dateStr) => {
  const d = parseRaceDate(dateStr);
  if (!d) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

const parseEt = (v) => {
  if (v == null || v === "NA" || v === "-" || v === "") return null;
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
};

const hasQuarterMileEt = (ft4) => {
  const n = parseEt(ft4);
  return n != null && n > 0;
};

// Shared distance decision: a race is treated as 1/4 mile when it ran today
// (assume full track) or it has a real 1/4-mile ET; otherwise it's 1/8 mile.
// Returns the matching class-table entry + which distance applies, or null
// when the class isn't in the table.
const resolve = (carClass, raceDate, ft4) => {
  const entry = lookupClass(carClass);
  if (!entry) return null;
  const isQuarter = isToday(raceDate) || hasQuarterMileEt(ft4);
  return { index: isQuarter ? entry.quarter : entry.eighth, isQuarter };
};

/**
 * @param {string} carClass  e.g. "SS/AH"
 * @param {string} raceDate  date string for the run (slip date / live timestamp)
 * @param {string|number} ft4  the 1/4-mile ET value for the run
 * @returns {string|null} index value to 2 decimals, or null if unavailable
 */
export const getClassIndex = (carClass, raceDate, ft4) => {
  const r = resolve(carClass, raceDate, ft4);
  return r && r.index != null ? r.index.toFixed(2) : null;
};

/**
 * Over / Under in whole milliseconds (numeric) = final ET − index, on the same
 * distance as the index (1/4-mile ET when the race is 1/4 mile, otherwise
 * 1/8-mile ET). Negative means under the index. Use this when a number is
 * needed (e.g. sorting a qualifying sheet).
 * @param {string} carClass
 * @param {string} raceDate
 * @param {string|number} ft4  1/4-mile ET
 * @param {string|number} ft8  1/8-mile ET
 * @returns {number|null} difference in whole milliseconds, or null if unavailable
 */
export const getOverUnderMs = (carClass, raceDate, ft4, ft8) => {
  const r = resolve(carClass, raceDate, ft4);
  if (!r || r.index == null) return null;
  const finalEt = parseEt(r.isQuarter ? ft4 : ft8);
  if (finalEt == null) return null;
  return Math.round((finalEt - r.index) * 1000);
};

/**
 * Over / Under formatted for display, e.g. "-015" / "020" (whole milliseconds,
 * signed, zero-padded to 3 digits). Wraps getOverUnderMs so the numeric and
 * formatted values never drift.
 * @param {string} carClass
 * @param {string} raceDate
 * @param {string|number} ft4  1/4-mile ET
 * @param {string|number} ft8  1/8-mile ET
 * @returns {string|null} formatted difference, or null if unavailable
 */
export const getOverUnder = (carClass, raceDate, ft4, ft8) => {
  const ms = getOverUnderMs(carClass, raceDate, ft4, ft8);
  if (ms == null) return null;
  return (ms < 0 ? "-" : "") + String(Math.abs(ms)).padStart(3, "0");
};

/**
 * NHRA category for a class, from the index table.
 * @param {string} carClass  e.g. "SS/AH"
 * @returns {string|null} "Stock" | "Super Stock" | "Comp", or null if the class
 *   isn't in the table.
 */
export const getCategoryForClass = (carClass) => {
  const entry = lookupClass(carClass);
  return entry ? entry.category : null;
};

/**
 * The distance-appropriate final ET as a number: the 1/4-mile ET when the race
 * is treated as 1/4 mile, otherwise the 1/8-mile ET. This is the ET the index
 * and over/under are measured against.
 * @param {string} carClass
 * @param {string} raceDate
 * @param {string|number} ft4  1/4-mile ET
 * @param {string|number} ft8  1/8-mile ET
 * @returns {number|null} final ET in seconds, or null if unavailable
 */
export const getFinalEt = (carClass, raceDate, ft4, ft8) => {
  const r = resolve(carClass, raceDate, ft4);
  if (!r) return null;
  return parseEt(r.isQuarter ? ft4 : ft8);
};
